import { useMemo } from 'react'
import { useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import type { ActionCenterLocationsQuery } from 'app/generated/gql/graphql'

export type LocationInfo = {
  name: string
  primaryTrackingNumber?: string
  callRailCompanyId?: string
}

const LOCATIONS_QUERY = graphql(`
  query ActionCenterLocations($pagination: PaginationInput) {
    locations(pagination: $pagination) {
      hits {
        id
        name
        primaryTrackingNumber
        callRailCompanyId
      }
    }
  }
`)

// Referentially stable on purpose: the focus card mounts this hook per
// contact, and an unstable variables object once spun it at 250+ renders/sec.
const VARIABLES = { pagination: { limit: 500 } }

/** id → location info for the session's locations. One cached document per workspace session. */
export function useLocationsInfo(): Map<string, LocationInfo> | undefined {
  const [result] = useQuery<ActionCenterLocationsQuery>({ query: LOCATIONS_QUERY, variables: VARIABLES })
  const hits = result.data?.locations.hits
  return useMemo(() => {
    if (!hits) return undefined
    return new Map(
      hits
        .filter((hit) => hit.id)
        .map((hit) => [
          hit.id,
          {
            name: hit.name || hit.id,
            primaryTrackingNumber: hit.primaryTrackingNumber ?? undefined,
            callRailCompanyId: hit.callRailCompanyId ?? undefined,
          },
        ]),
    )
  }, [hits])
}
